import {
  Box,
  Typography,
  CardContent,
  CardActions,
  Button,
  Skeleton,
} from '@mui/material';

import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBlankAssistantApi, saveWhatsappDetails } from '../../utils/apis';
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAgent } from '../../utils/redux/slices/updateagentSlice';
import { useTemplates } from '../../hooks/useTemplates';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../../components/common/MessagePopup';
import AgentChannelSelectionModal from './AgentChannelSelection';

interface LandingPageProps {
  onTemplateSelect: (template: any) => void;
  /** When true, renders without forcing full viewport height (for embedded wizards). */
  embedded?: boolean;
}

const LandingPage: FC<LandingPageProps> = ({ onTemplateSelect, embedded }) => {
  const dispatch = useDispatch();
  const [processingTemplateId, setProcessingTemplateId] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  const { data, isLoading } = useTemplates();
  const { alert, setAlert, showAlert } = useAlert();
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const { mutateAsync: createBlankAssistant } = useMutation({
    mutationFn: createBlankAssistantApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: error => {
      console.log(error);
    },
  });

  // Define custom button colors for each template type
  const buttonColors = {
    'food ordering': '#3D9970', // Green
    'automate reservation': '#3D85C6', // Blue
    'general queries': '#5B4CE3', // Purple
    default: '#9C27B0', // Fallback color
  };

  const getButtonColor = (templateName: string) => {
    const lowerName = templateName.toLowerCase();
    return (
      buttonColors[lowerName as keyof typeof buttonColors] ||
      buttonColors.default
    );
  };

  const handleTemplateClick = (template: any) => {
    setSelectedTemplate(template);
    setShowChannelModal(true);
  };

  const handleChannelSelection = async (data: {
    channel: 'whatsapp' | 'voice';
    numbers?: string[];
    whatsappPhoneNumberId?: string;
    whatsappToken?: string;
    whatsappVerifyToken?: string;
  }) => {
    setProcessingTemplateId(selectedTemplate._id);
    try {
      // First create blank assistant
      const response = await createBlankAssistant({
        templateId: selectedTemplate._id,
        agentChannel: data.channel,
      });

      // If WhatsApp channel is selected, save WhatsApp details
      if (data.channel === 'whatsapp' && response.data?.assistant?._id) {
        await saveWhatsappDetails({
          agentId: response.data.assistant._id,
          whatsappPhonenumberId: data.whatsappPhoneNumberId || '',
          whatsappToken: data.whatsappToken || '',
          whatsappVerificationToken: data.whatsappVerifyToken || '',
        });
      }

      dispatch(setAgent(response.data?.assistant));
      onTemplateSelect(selectedTemplate);
    } catch (error) {
      showAlert(
        // @ts-expect-error
        error?.response?.data?.message || 'Failed to create assistant',
        'error'
      );
    } finally {
      setProcessingTemplateId(null);
      setShowChannelModal(false);
    }
  };

  const sortedTemplates = (data?.data?.templates || []).sort(
    (a: any, b: any) => {
      const order = [
        'food ordering',
        'automate reservation',
        'general queries',
      ];
      return (
        order.indexOf(a.name.toLowerCase()) -
        order.indexOf(b.name.toLowerCase())
      );
    }
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          px: { xs: 2, sm: 4, md: 10 },
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        {/* Header Section Skeleton */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: { xs: 3, sm: 4, md: 4 },
            width: '100%',
          }}
        >
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={24} />
        </Box>

        {/* Template Cards Skeleton */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
          }}
        >
          {Array.from(new Array(3)).map((_, index) => (
            <Box
              key={index}
              sx={{
                border: '1.5px solid lightgray',
                borderRadius: '12px',
                p: { xs: 2, md: 3 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />

              {/* Feature list skeletons */}
              {Array.from(new Array(3)).map((_, featureIndex) => (
                <Box
                  key={featureIndex}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={20}
                    sx={{ ml: 1 }}
                  />
                </Box>
              ))}

              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={40}
                  sx={{ borderRadius: '6px' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
      <AgentChannelSelectionModal
        open={showChannelModal}
        onClose={() => setShowChannelModal(false)}
        onContinue={handleChannelSelection}
        loading={!!processingTemplateId}
      />
      <Box
        sx={{
          px: { xs: 2, sm: 4, md: 10 },
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: embedded ? 'auto' : '100vh',
          py: embedded ? 3 : undefined,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: { xs: 3, sm: 4, md: 4 },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 24, md: 28 },
              letterSpacing: '-1%',
              color: 'custom.textBlue',
              mb: 0.5,
              textAlign: 'center',
            }}
          >
            Choose Your AI Agent
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              fontSize: { xs: 14, md: 16 },
              textAlign: 'center',
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Select the type of agent that best fits your business needs
          </Typography>
        </Box>

        {/* Template Cards Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'auto', md: 'calc(100vh - 200px)' },
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
              boxShadow: 'none',
            }}
          >
            {sortedTemplates.map((template: any) => {
              const buttonColor = getButtonColor(template.name);
              return (
                <Box
                  key={template._id}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1.5px solid`,
                    borderColor: 'lightgray',
                    borderRadius: '12px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: 16, md: 18 },
                          color: 'custom.textBlue',
                          letterSpacing: '-1%',
                        }}
                      >
                        {template.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: 11, md: 12 },
                        fontWeight: 400,
                        letterSpacing: '-1%',
                        lineHeight: 1.5,
                        mb: 2,
                      }}
                    >
                      {template.description}
                    </Typography>
                    <Box>
                      {template.properties.map(
                        (feature: string, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '16px 1fr',
                              mb: 1,
                            }}
                          >
                            <CheckCircle
                              size={16}
                              weight="bold"
                              color="green"
                              style={{ marginTop: '4px' }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: 12, md: 14 },
                                fontWeight: 400,
                                letterSpacing: '-1%',
                                color: 'custom.textBlue',
                                // lineHeight: 1.4,
                                ml: 1,
                              }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 1, pt: 0 }}>
                    {' '}
                    {/* Reduced padding */}
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        py: 1,
                        borderRadius: '6px',
                        boxShadow: 'none',
                        backgroundColor: buttonColor,
                        '&:hover': {
                          backgroundColor: buttonColor,
                        },
                      }}
                      onClick={() => handleTemplateClick(template)}
                      endIcon={<ArrowRight />}
                    >
                      {template.btnText}
                    </Button>
                  </CardActions>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
